import React, { useState, useEffect } from 'react'
import { apiGet, apiPost } from '../../utils/api'

interface Article {
  id: string
  title: string
  body: string
  category?: string
  upvotes: number
}

interface ArticleSharePanelProps {
  chatId: string
  isOpen: boolean
  onClose: () => void
  onShared: () => void
}

export const ArticleSharePanel: React.FC<ArticleSharePanelProps> = ({ chatId, isOpen, onClose, onShared }) => {
  const [articles, setArticles] = useState<Article[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      apiGet<{ articles: Article[] }>(`/articles${search ? `?search=${encodeURIComponent(search)}` : ''}`)
        .then(r => setArticles(r.articles))
        .catch(() => {})
        .finally(() => setLoading(false))
    }
  }, [isOpen, search])

  const handleShare = async (articleId: string) => {
    try {
      await apiPost(`/chats/${chatId}/share-article`, { article_id: articleId })
      onShared()
      onClose()
    } catch (err) {
      console.error('Failed to share article:', err)
    }
  }

  if (!isOpen) return null

  return (
    <div className="border-t border-border bg-surface max-h-64 overflow-hidden flex flex-col animate-slide-up">
      <div className="px-4 py-2 border-b border-border flex items-center gap-2">
        <svg className="w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search articles..."
          className="flex-1 text-sm bg-transparent outline-none text-txt-primary placeholder-txt-tertiary"
          autoFocus
        />
        <button onClick={onClose} className="text-txt-tertiary hover:text-txt-primary">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="py-8 text-center text-sm text-txt-secondary">Loading articles...</div>
        ) : articles.length === 0 ? (
          <div className="py-8 text-center text-sm text-txt-secondary">No articles found</div>
        ) : (
          articles.map(article => (
            <button
              key={article.id}
              onClick={() => handleShare(article.id)}
              className="w-full text-left px-4 py-2.5 hover:bg-surface-hover border-b border-border/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-txt-primary">{article.title}</span>
                {article.category && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-tertiary text-txt-secondary">{article.category}</span>
                )}
              </div>
              <p className="text-xs text-txt-tertiary mt-0.5 line-clamp-1">{article.body.substring(0, 120)}</p>
            </button>
          ))
        )}
      </div>
    </div>
  )
}
